import { vec2 } from "@tlaukkan/tsm";
import { dir } from "console";
import { Entity, Not, System } from "ecsy";
import { applyForce, applyImpulse, claimVec2, passVec2 } from "../box2dUtils";
import { AimForPlayer, Antibody, ArenaLimit, AvoidMotion, Bullet, Collision, ContinuousMotion, Cooldown, ImpulseMotion, OnMoveDistance, OrientationControl, Physics, Sentry, SineWaveMotion, Slowed, SprinklerMotion, Stunned, SuspendMovement, Sync, Targeting, TimedLifespan, TorqueOrientationControl, Transform, UnstoppableMotion } from "../components";
import { PhysicsConstants } from "../constants";
import { getEntityById, getId } from "../entityFactory";
import { printEntityTrace } from "../entityTrace";
import { Game } from "../game";
import { MoveTriggerReceipt } from "../messageTypes";
import { triggerEffect } from "../triggeredEffects";
import { angleBetween, angleFromUp, clamp, perpendicular, printObjectProperties, smallestAngleBetween, vecToStr } from "../utils";


export class MovementSystem extends System {

	game: Game;

	moveTriggerQueue: MoveTriggerReceipt[] = [];//Synced between client and server by sync systems

	constructor(world: any, attributes: any) {
		// @ts-ignore
		super(world, attributes);
		this.game = attributes.game;
	}

	execute(delta: number, time: number) {
		if (!this.game.inSimulation) return;
		this.queries.avoider.results.forEach(entity => {
			const avoider = entity.getComponent(AvoidMotion);
			const transform = entity.getComponent(Transform);
			const targeting = entity.getComponent(Targeting);
			const antibodyTargets = targeting.getTargets(avoider.antibodyTargetingIndex) || [];
			const nearestAntibody = antibodyTargets[0] || null;

			const lifespan = entity.getMutableComponent(TimedLifespan);
			const livedSteps = getStepNumber() - lifespan.firstStep;
			let force = avoider.force;
			force = avoider.force * (1 - livedSteps / lifespan.stepsToLive);

			const accelAdjustment = entity.getComponent(Slowed)?.speedReductionPercent || 1;

			if (nearestAntibody) {
				const antibodyPos = nearestAntibody.getComponent(Transform).position;
				const antibodyDir = vec2.direction(transform.position, antibodyPos);
				applyForce(entity, antibodyDir.scale(force * accelAdjustment));
			}
		});

		//Movement
		this.queries.continuous.results.forEach(entity => {
			const movement = entity.getComponent(ContinuousMotion);
			const transform = entity.getComponent(Transform);
			const physics = entity.getComponent(Physics);

			const speedAdjustment = entity.getComponent(Slowed)?.speedReductionPercent || 1;

			//Normally acceleration is divided by mass, multiply here so this accel value ignores object mass
			const acceleration = transform.direction.copy().scale(movement.accel * physics.mass);

			const currentVel = claimVec2(physics.body.GetLinearVelocity());
			const predictedAccerationThisFrame = acceleration.copy().scale(PhysicsConstants.StepRate / physics.mass);
			const expectedVelocity = vec2.sum(predictedAccerationThisFrame, currentVel); //does not account for damping
			const expectedSpeed = expectedVelocity.length();

			if (expectedSpeed < movement.maxSpeed * speedAdjustment) {
				applyForce(entity, acceleration);
			}
			else {
				const currrentDirection = currentVel.normalize();
				const accelInCurrentDirection = currrentDirection.scale(vec2.dot(acceleration, currrentDirection));
				const accelInPerpendicular = vec2.difference(acceleration, accelInCurrentDirection);

				//TODO: Figure out how to do this right. Is this ok to not use the step rate and mass like above? Test with insane accel showed this doesn't work.
				//const currentSpeed = currentVel.length();
				// const maxAdditionalSpeedInCurrentDirection = movement.maxSpeed - currentSpeed;
				// if (maxAdditionalSpeedInCurrentDirection > 0) {
				// 	const speedInDirection = accelInCurrentDirection.length();
				// 	let applicableAcceleration = maxAdditionalSpeedInCurrentDirection;
				// 	if (speedInDirection > 0) applicableAcceleration /= speedInDirection;
				// 	accelInCurrentDirection.scale(applicableAcceleration);
				// 	applyForce(entity, accelInCurrentDirection);
				// }

				applyForce(entity, accelInPerpendicular);
			}
		});


		//TODO: Should Impulse movement should have a max speed and be affected by slowed?
		this.queries.impulse.results.forEach(entity => {
			const movement = entity.getComponent(ImpulseMotion);
			if (movement.amount === 0) return;
			else --movement.amount;

			const transform = entity.getComponent(Transform);
			const cooldown = movement.canRepeatOnCooldown ? entity.getComponent(Cooldown) : null;

			if (!cooldown || cooldown.done) {
				const impulse = transform.direction.copy().scale(movement.force);
				applyImpulse(entity, impulse);
				if (cooldown) cooldown.restart();
			}
		});

		this.queries.unstoppable.results.forEach(entity => {
			const movement = entity.getComponent(UnstoppableMotion);
			const transform = entity.getComponent(Transform);
			const physics = entity.getComponent(Physics);
			physics.body.SetLinearVelocity(passVec2(transform.direction.copy().scale(movement.speed)));
		});

		this.queries.wave.results.forEach(entity => {
			const transform = entity.getComponent(Transform);
			const sine = entity.getMutableComponent(SineWaveMotion);
			const accelAdjustment = entity.getComponent(Slowed)?.speedReductionPercent || 1;
			const accel = sine.accel * accelAdjustment;

			const quarterPeriod = sine.period / 4;
			const sign = sine.progress > -quarterPeriod ? 1 : -1; //starting point should be halfway through rise

			const lateral = perpendicular(transform.direction.normalize()).scale(sign * accel * sine.slope);
			const forward = transform.direction.copy().normalize().scale(accel);
			applyForce(entity, forward);
			applyForce(entity, lateral);
			sine.progress += PhysicsConstants.StepRate;
			if (sine.progress > quarterPeriod) {
				sine.progress = sine.progress - sine.period;
			}
		});



		//Rotation
		this.queries.orientationControl.results.forEach(entity => {
			const transform = entity.getComponent(Transform);
			const physics = entity.getComponent(Physics);
			const orientation = entity.getComponent(OrientationControl);

			if (transform.direction.equals(vec2.zero)) return;
			const targetAngle = angleFromUp(transform.direction);
			const currentAngle = physics.body.GetAngle();
			const difference = smallestAngleBetween(currentAngle, targetAngle);

			//Hard-snaps angular velocity to close the gap this step. Fine as long as nothing is welded to this
			//body - see TorqueOrientationControl for entities that need to play nice with a weld joint.
			if (Math.abs(difference) > orientation.tolerance) {
				const snapVelocity = Math.abs(difference) / PhysicsConstants.StepRate;
				const speed = Math.sign(difference) * Math.min(snapVelocity, orientation.turnSpeed);
				physics.body.SetAngularVelocity(speed);
			}
			else {
				physics.body.SetAngularVelocity(0);
			}
		});

		this.queries.torqueOrientationControl.results.forEach(entity => {
			const transform = entity.getComponent(Transform);
			const physics = entity.getComponent(Physics);
			const orientation = entity.getComponent(TorqueOrientationControl);

			let targetDirection = transform.direction;

			if (!orientation.fixedDirection.equals(vec2.zero)) {
				targetDirection = orientation.fixedDirection;
			}

			if (targetDirection.equals(vec2.zero)) return;
			const targetAngle = angleFromUp(targetDirection);
			const currentAngle = physics.body.GetAngle();
			const difference = smallestAngleBetween(currentAngle, targetAngle);

			const speedFraction = clamp(Math.abs(difference) / Math.PI, 0, 1);
			const desiredAngularVelocity = Math.sign(difference) * orientation.turnSpeed * speedFraction;
			const currentAngularVelocity = physics.body.GetAngularVelocity();
			const maxDelta = orientation.turnAccel * PhysicsConstants.StepRate;
			const angularVelocityDelta = clamp(desiredAngularVelocity - currentAngularVelocity, -maxDelta, maxDelta);
			const torque = angularVelocityDelta * physics.body.GetInertia() / PhysicsConstants.StepRate;
			physics.body.ApplyTorque(torque, true);
		});

		this.queries.bullets.added?.forEach(entity => {
			const physics = entity.getComponent(Physics);
			physics.body.SetLinearDamping(0);
		});

		this.queries.sprinkers.added?.forEach(entity => {
			const transform = entity.getComponent(Transform);
			const sprinker = entity.getComponent(SprinklerMotion);
			const startingRotation = transform.rotation;
			sprinker.startingRotation = startingRotation;
		});

		for (const entity of this.queries.sprinkers.results) {
			const transform = entity.getComponent(Transform);
			const physics = entity.getComponent(Physics);
			const sprinkler = entity.getComponent(SprinklerMotion);
			const angle = sprinkler.startingRotation - transform.rotation;
			if (Math.abs(angle) > sprinkler.maxAngle && !sprinkler.turningInProgress) {
				sprinkler.direction = -sprinkler.direction;
				sprinkler.turningInProgress = true;
			}
			else if (Math.abs(angle) < sprinkler.maxAngle) {
				sprinkler.turningInProgress = false;
			}

			//TODO: Should we be using set velocity here?
			physics.body.SetAngularVelocity(sprinkler.turnSpeed * sprinkler.direction);
		}

		//Movement trigger
		if (process.env.is_server) {
			this.moveTriggerQueue.length = 0;
			for (const entity of this.queries.moveTriggers.results) {
				const trigger = entity.getComponent(OnMoveDistance);
				const physics = entity.getComponent(Physics);

				//Use velocity not position so we aren't measuring differently on curved or back and forth paths.
				const speed = claimVec2(physics.body.GetLinearVelocity()).length();
				trigger.progress += speed * PhysicsConstants.StepRate;

				if (trigger.progress > trigger.distance) {
					trigger.progress = trigger.progress - trigger.distance;
					const id = getId(entity);
					if (id != null) {
						//Queue a receipt for ServerSyncSystem to broadcast to clients
						this.moveTriggerQueue.push({ id, stepNumber: getStepNumber(), isMoveTrigger: true });
						triggerEffect(trigger.effect, entity);
					}
				}
			}
		}
		else { //client
			for (const triggerMessage of this.moveTriggerQueue) {
				//Server told us a movement trigger fired on this entity; apply the effect locally
				const entity = getEntityById(triggerMessage.id);
				if (entity && entity.hasComponent(OnMoveDistance)) {
					const trigger = entity.getComponent(OnMoveDistance);
					triggerEffect(trigger.effect, entity);
				}
			}
			this.moveTriggerQueue.length = 0;
		}

		//Un-stun after a delay
		const entitiesToUnstun = new Array<Entity>();
		for (const entity of this.queries.stunned.results) {
			const stunned = entity.getComponent(Stunned);
			stunned.duration -= PhysicsConstants.StepRate;
			if (stunned.duration < 0) {
				entitiesToUnstun.push(entity);
			}
		}
		entitiesToUnstun.forEach(entity => entity.removeComponent(Stunned));

		//Suspend movement stores and restores movement after a delay
		this.queries.suspend.added?.forEach(entity => {
			const suspend = entity.getComponent(SuspendMovement);
			const physics = entity.getComponent(Physics);
			if (!physics) return;
			suspend.previousLinearV = claimVec2(physics.body.GetLinearVelocity());
			suspend.previousAngularV = physics.body.GetAngularVelocity();
			physics.body.SetLinearVelocity(passVec2(vec2.zero));
			physics.body.SetAngularVelocity(0);
		});

		const entitiesToUnsuspend = new Array<Entity>();
		for (const entity of this.queries.suspend.results) {
			const suspend = entity.getComponent(SuspendMovement);
			suspend.duration -= PhysicsConstants.StepRate;
			if (suspend.duration < 0) {
				entitiesToUnsuspend.push(entity);
			}
		}
		entitiesToUnsuspend.forEach(entity => entity.removeComponent(SuspendMovement));

		this.queries.suspend.removed?.forEach(entity => {
			const suspend = entity.getRemovedComponent(SuspendMovement) || entity.getComponent(SuspendMovement);
			const physics = entity.getComponent(Physics);
			if (!physics) return;
			const currentLinearV = claimVec2(physics.body.GetLinearVelocity());
			const originalSpeedMagnitude = suspend.previousLinearV.length();
			const newLinearV = currentLinearV.add(suspend.previousLinearV)
			if (newLinearV.length() > originalSpeedMagnitude) {
				newLinearV.normalize().scale(originalSpeedMagnitude);
			}
			physics.body.SetLinearVelocity(passVec2(newLinearV));
			physics.body.SetAngularVelocity(suspend.previousAngularV);
		});

		const playerSpawnCenters = this.game.players.map(player => new vec2(player.spawnPoint));
		if (playerSpawnCenters.length > 0) {
			for (const entity of this.queries.arenaLimited.results) {
				const transform = entity.getComponent(Transform);
				const arenaLimit = entity.getComponent(ArenaLimit);
				const physics = entity.getComponent(Physics);

				// Viruses pull toward their aim target; everything else toward nearest spawn center
				let center: vec2;
				const aim = entity.getComponent(AimForPlayer);
				if (aim?.target?.hasComponent(Transform)) {
					center = aim.target.getComponent(Transform).position;
				} else {
					center = playerSpawnCenters[0];
					let minSqrDist = Infinity;
					for (const sc of playerSpawnCenters) {
						const d = vec2.squaredDistance(transform.position, sc);
						if (d < minSqrDist) { minSqrDist = d; center = sc; }
					}
				}

				const toCenter = center.copy().subtract(transform.position);
				const distToCenter = toCenter.length();
				if (distToCenter === 0) continue;

				// Shrink effective limit based on outward speed so fast-moving entities brake earlier
				let effectiveLimit = arenaLimit.maxDistance;
				if (arenaLimit.minDistance > 0) {
					const velocity = claimVec2(physics.body.GetLinearVelocity());
					const outwardSpeed = Math.max(0, -vec2.dot(velocity, toCenter) / distToCenter);
					effectiveLimit = Math.max(arenaLimit.minDistance, arenaLimit.maxDistance - outwardSpeed);
				}

				if (distToCenter > effectiveLimit) {
					const urgency = 1 - ((effectiveLimit - arenaLimit.minDistance) / (arenaLimit.maxDistance - arenaLimit.minDistance));
					const forceAtMaxUrgency = 500;
					applyForce(entity, toCenter.scale(arenaLimit.forcePerExcessDistance + (urgency * forceAtMaxUrgency)));
				}
			}
		}
	}
}

MovementSystem.queries = {
	avoider: { components: [Physics, Sync, AvoidMotion, Targeting, TimedLifespan, Not(Stunned), Not(SuspendMovement)], },
	wave: { components: [Physics, Sync, SineWaveMotion, Not(Stunned), Not(SuspendMovement)] },
	continuous: { components: [Physics, ContinuousMotion, Not(Stunned), Not(SuspendMovement)] },
	impulse: { components: [Physics, ImpulseMotion, Not(Stunned), Not(SuspendMovement)], listen: { added: true, removed: true } },
	unstoppable: { components: [Physics, UnstoppableMotion, Not(Stunned), Not(SuspendMovement)], listen: { added: true, removed: true } },

	stunned: { components: [Physics, Stunned] },
	suspend: { components: [Physics, SuspendMovement], listen: { added: true, removed: true } },

	bullets: { components: [Physics, Bullet], listen: { added: true } },

	orientationControl: { components: [Physics, Transform, OrientationControl] },
	torqueOrientationControl: { components: [Physics, Transform, TorqueOrientationControl] },
	sprinkers: { components: [Physics, SprinklerMotion, Transform], listen: { added: true } },
	moveTriggers: { components: [Physics, OnMoveDistance, Transform] },

	arenaLimited: { components: [Physics, Transform, ArenaLimit] },
};
